import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Button from '@/components/Button';
import Modal from '@/components/Modals/Modal';

import { Dimensions, useDimensionsModal } from './use-dimensions-modal';

export type { Dimensions };

export interface DimensionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (dims: Dimensions) => void;
  loading?: boolean;
  initialDimensions?: Dimensions | null;
  title?: string;
  description?: string;
  confirmText?: string;
  loadingText?: string;
}

const DimensionsModal = (props: DimensionsModalProps) => {
  const hooks = useDimensionsModal(props);

  const modalTitle = props.title ?? 'Dimensiones del producto';
  const modalDescription =
    props.description ??
    (props.initialDimensions
      ? 'Revisa y ajusta las dimensiones del producto para continuar.'
      : 'Este producto no tiene dimensiones registradas. Ingrésalas para continuar con la sincronización.');
  const confirmLabel = props.loading
    ? (props.loadingText ?? 'Sincronizando…')
    : (props.confirmText ?? 'Confirmar y sincronizar');

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={hooks.handleClose}
      title={modalTitle}
      maxWidth="sm"
      actions={
        <Button
          variant="contained"
          onClick={hooks.handleConfirm}
          disabled={props.loading || !hooks.isValid}
        >
          {confirmLabel}
        </Button>
      }
    >
      <Box mb={1}>
        <Typography variant="body2" color="text.secondary">
          {modalDescription}
        </Typography>
      </Box>

      <Grid container spacing={2} mt={1}>
        <Grid size={{ xxs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Alto (cm)"
            type="number"
            value={hooks.fields.height}
            onChange={(e) => hooks.handleChange('height', e.target.value)}
            onBlur={() => hooks.handleBlur('height')}
            error={hooks.fieldError('height')}
            helperText={hooks.fieldError('height') ? 'Ingresa un valor entero positivo' : undefined}
            slotProps={{ htmlInput: { min: 1, step: 1 } }}
          />
        </Grid>
        <Grid size={{ xxs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Ancho (cm)"
            type="number"
            value={hooks.fields.width}
            onChange={(e) => hooks.handleChange('width', e.target.value)}
            onBlur={() => hooks.handleBlur('width')}
            error={hooks.fieldError('width')}
            helperText={hooks.fieldError('width') ? 'Ingresa un valor entero positivo' : undefined}
            slotProps={{ htmlInput: { min: 1, step: 1 } }}
          />
        </Grid>
        <Grid size={{ xxs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Largo (cm)"
            type="number"
            value={hooks.fields.length}
            onChange={(e) => hooks.handleChange('length', e.target.value)}
            onBlur={() => hooks.handleBlur('length')}
            error={hooks.fieldError('length')}
            helperText={hooks.fieldError('length') ? 'Ingresa un valor entero positivo' : undefined}
            slotProps={{ htmlInput: { min: 1, step: 1 } }}
          />
        </Grid>
        <Grid size={{ xxs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Peso (g)"
            type="number"
            value={hooks.fields.weight}
            onChange={(e) => hooks.handleChange('weight', e.target.value)}
            onBlur={() => hooks.handleBlur('weight')}
            error={hooks.fieldError('weight')}
            helperText={hooks.fieldError('weight') ? 'Ingresa un valor entero positivo' : undefined}
            slotProps={{ htmlInput: { min: 1, step: 1 } }}
          />
        </Grid>
      </Grid>
    </Modal>
  );
};

export default DimensionsModal;
